import { Botao } from '@baiocchi/ui'
import type { ReactNode } from 'react'

import { IconeLua, IconeSol } from '../../componentes/icones'
import { useTema } from '../../ganchos/usar-tema'

/**
 * Moldura das telas de acesso (TELA-00, 00A, 00B).
 *
 * Duas colunas no desktop: formulário à esquerda, painel de marca à direita. No
 * celular o painel de marca sai inteiro — em 360px de largura ele empurraria o
 * campo de senha para fora da primeira dobra, e a tela de login existe para uma
 * coisa só.
 */
export function MolduraAutenticacao({
  titulo,
  descricao,
  children,
  rodape,
}: {
  titulo: string
  descricao?: ReactNode
  children: ReactNode
  rodape?: ReactNode
}) {
  const { escuro, alternar } = useTema()

  return (
    <div className="flex min-h-screen bg-fundo">
      <div className="flex min-w-0 flex-1 flex-col px-5 py-8 sm:px-10">
        <header className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2.5">
            <img
              src="/logo.png"
              alt="Baiocchi Leads"
              className="logo-marca h-10 w-10 rounded-md object-cover"
              style={{ objectPosition: '50% 32%' }}
            />
            <span className="flex flex-col leading-none">
              <span className="text-sm font-bold uppercase tracking-wide text-conteudo">
                Baiocchi
              </span>
              <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-marca-500">
                Leads
              </span>
            </span>
          </span>

          <Botao
            variante="fantasma"
            tamanho="icone"
            onClick={alternar}
            aria-label={escuro ? 'Usar tema claro' : 'Usar tema escuro'}
          >
            {escuro ? <IconeSol /> : <IconeLua />}
          </Botao>
        </header>

        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-sm">
            <h1 className="text-2xl font-semibold tracking-tight text-conteudo">{titulo}</h1>
            {descricao ? <p className="mt-2 text-sm text-conteudo-suave">{descricao}</p> : null}
            <div className="mt-7">{children}</div>
            {rodape ? <div className="mt-6 text-sm text-conteudo-suave">{rodape}</div> : null}
          </div>
        </div>

        <footer className="text-xs text-conteudo-fraco">
          Baiocchi Imóveis · uso restrito a colaboradores autorizados
        </footer>
      </div>

      {/* Painel de marca — só no desktop. */}
      <aside className="relative hidden w-[46%] max-w-2xl overflow-hidden bg-grafite-900 lg:block">
        {/**
         * Fundo de marca. Dois focos radiais de laranja sobre grafite, e não um
         * gradiente linear: o linear cria uma faixa visível de transição, que num
         * painel deste tamanho aparece como listra.
         */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(130% 100% at 78% 4%, rgb(255 122 53 / 0.95) 0%, rgb(242 101 11 / 0.55) 32%, transparent 66%), radial-gradient(95% 75% at 8% 96%, rgb(242 101 11 / 0.3) 0%, transparent 62%)',
          }}
        />
        {/* Véu escuro no rodapé para o texto ter contraste sobre o laranja. */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-2/3"
          style={{
            background: 'linear-gradient(to top, rgb(26 29 31 / 0.85) 0%, transparent 100%)',
          }}
        />
        <div className="relative flex h-full flex-col justify-end p-12">
          <p className="text-3xl font-semibold leading-tight text-white">
            Todo lead atendido.
            <br />
            Todo atendimento registrado.
          </p>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-white/70">
            O CRM de leads da Baiocchi Imóveis: painel de atendimentos por temperatura,
            histórico imutável por cliente e agenda integrada ao lead.
          </p>
        </div>
      </aside>
    </div>
  )
}
